import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { US_STATES } from "@/lib/us-states";
import { US_CITIES_VALID } from "@/data/us-cities";
import { stateNameSlug, toSlug } from "@/lib/seo-slugs";
import {
  breadcrumbListSchema,
  collectionPageSchema,
  jsonLdScript,
} from "@/lib/seo-schema";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/locations/")({
  head: () => {
    const title = "Browse Doctors by Location | Heallexa";
    const description =
      "Find healthcare providers by state and city across all 50 U.S. states. Heallexa lists 6.3 million+ verified doctors, dentists, and specialists — searchable by location, specialty, and insurance.";
    const url = `${SITE}/locations`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        jsonLdScript(collectionPageSchema({ name: title, description, url })),
        jsonLdScript(
          breadcrumbListSchema([
            { name: "Home", url: "/" },
            { name: "Locations", url: "/locations" },
          ]),
        ),
      ],
    };
  },
  component: LocationsIndex,
});

function LocationsIndex() {
  const cities = [...US_CITIES_VALID].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-6xl px-6 py-16">
        <Breadcrumbs
          className="mb-6"
          items={[{ name: "Home", href: "/" }, { name: "Locations" }]}
        />
        <header className="mb-12">
          <p className="text-sm font-medium text-primary mb-2">Heallexa Directory</p>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-foreground">
            Browse Doctors by Location
          </h1>
          <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
            Search <strong className="text-foreground font-medium">6.3 million+ verified healthcare providers</strong> across
            all 50 U.S. states. Every listing is sourced from the federal NPI registry — the
            authoritative record of licensed U.S. providers. Select a state to browse by city,
            or jump straight to one of {cities.length}+ major cities below.
          </p>
        </header>

        <section className="mb-14">
          <h2 className="text-xl font-semibold text-foreground mb-4">All States</h2>
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-y-2 gap-x-6">
            {US_STATES.map((s) => (
              <li key={s.code}>
                <Link
                  to="/locations/$state"
                  params={{ state: stateNameSlug(s.name) }}
                  className="text-sm text-foreground hover:text-primary hover:underline transition"
                >
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-foreground mb-4">
            Popular Cities ({cities.length})
          </h2>
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-y-2 gap-x-6">
            {cities.map((c) => {
              const st = US_STATES.find((s) => s.code === c.state);
              if (!st) return null;
              return (
                <li key={`${c.name}-${c.state}`}>
                  <Link
                    to="/locations/$state/$city"
                    params={{ state: stateNameSlug(st.name), city: toSlug(c.name) }}
                    search={{ page: undefined }}
                    className="text-sm text-muted-foreground hover:text-primary hover:underline transition"
                  >
                    {c.name}, {c.state}
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
